import { useId, type ReactElement } from "react";
import { barFill } from "./bar.ts";
import { KIT_METRICS, logical } from "./metrics.ts";

/**
 * A progress bar with its label (brief section 5.1), such as the loading shell's: the bar track over the label
 * in the pixel face, as the design draws it (ADR-061's amendment "the game menu, character select, loading and
 * confirm dialog match the design"), with a count at the end of the label's line when the consumer gives one
 * (the loading shell's finished steps), and the progress fill strip drawn across the whole inside of the track and
 * cropped to the value on whole logical pixels (`barFill`), so it is never scaled and keeps its highlight
 * and shade rows at any value (ADR-061, "bars crop, never scale"). It is a `progressbar` named by its
 * label, and it takes no pointer events. The label's line is a polite live region read whole, so a label or
 * a count that changes, as the loading shell's step and its finished steps do, is read out without focus;
 * the value alone changes only the bar, which assistive technology reports as a progress bar's value.
 */

/**
 * A progress bar.
 *
 * @param props The `label` the bar shows and is named by; the `count` shown after it, when there is one;
 *   its `value` and `max`, the value held to between 0 and `max` for assistive technology as the fill is;
 *   and its `width` in logical pixels.
 * @returns The bar and its label.
 * @throws RangeError when the width leaves the track no inside, which no fill could cover.
 * @example
 * <ProgressBar label={step} count={`${finished}/${steps}`} value={done} max={steps} width={240} />
 */
export function ProgressBar({ label, count, value, max, width }: { readonly label: string; readonly count?: string; readonly value: number; readonly max: number; readonly width: number }): ReactElement {
  const labelId = useId();
  const metrics = KIT_METRICS.bar;
  const inner = width - metrics.insets;
  if (inner <= 0) {
    throw new RangeError(`a bar ${width} logical pixels wide has no inside`);
  }
  const fill = barFill(value, max, inner);
  const top = Number.isFinite(max) && max > 0 ? max : 0;
  const shown = top === 0 || Number.isNaN(value) ? 0 : Math.min(Math.max(value, 0), top);
  return (
    <div className="ot-progress" data-kit="progress" style={{ width: logical(width), gap: logical(metrics.gap) }}>
      <div role="progressbar" aria-labelledby={labelId} aria-valuemin={0} aria-valuemax={top} aria-valuenow={shown} className="ot-progress__track ot-ui-frame-bar-track" data-kit="progress-track" style={{ height: logical(metrics.track) }}>
        <div className="ot-progress__crop" data-kit="progress-crop" data-fill={fill} style={{ width: logical(fill), height: logical(metrics.fill) }}>
          <div className="ot-progress__fill ot-ui-frame-bar-fill" style={{ width: logical(inner), height: logical(metrics.fill) }} />
        </div>
      </div>
      <div className="ot-progress__head" aria-live="polite" aria-atomic="true" style={{ gap: logical(metrics.countGap) }}>
        <span id={labelId} className="ot-progress__label">
          {label}
        </span>
        {count === undefined ? null : (
          <span className="ot-progress__count" data-kit="progress-count">
            {count}
          </span>
        )}
      </div>
    </div>
  );
}
